import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/Logo/logo.png";
import {
  FaHome,
  FaStar,
  FaDollarSign,
  FaInfoCircle,
  FaBookOpen,
  FaEnvelope,
  FaRocket,
  FaBars,
  FaTimes,
  FaChartLine,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { to: "/", label: "Home", icon: <FaHome className="inline mr-2 mb-1" /> },
  {
    to: "/features",
    label: "Features",
    icon: <FaStar className="inline mr-2 mb-1" />,
  },
  {
    to: "/roi",
    label: "ROI",
    icon: <FaChartLine className="inline mr-2 mb-1" />,
  },
  {
    to: "/pricing",
    label: "Pricing",
    icon: <FaDollarSign className="inline mr-2 mb-1" />,
  },
  {
    to: "/how-it-works",
    label: "How it works",
    icon: <FaInfoCircle className="inline mr-2 mb-1" />,
  },
  {
    to: "/about",
    label: "About",
    icon: <FaInfoCircle className="inline mr-2 mb-1" />,
  },
  {
    to: "/resources",
    label: "Resources",
    icon: <FaBookOpen className="inline mr-2 mb-1" />,
  },
  {
    to: "/contact",
    label: "Contact",
    icon: <FaEnvelope className="inline mr-2 mb-1" />,
  },
  
];

const Navbar: React.FC = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuToggle = () => setMenuOpen((open) => !open);
  const handleLinkClick = () => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <nav className="navbar fixed top-0 left-0 w-full bg-gradient-to-r from-blue-50 via-white to-blue-100 shadow-lg z-50 border-b border-blue-100">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3 flex justify-between items-center w-full">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center space-x-2 group min-w-0"
          onClick={handleLinkClick}
        >
          <img
            src={logo}
            alt="RoomRounds Logo"
            className="object-contain rounded-full border-2 border-blue-200 group-hover:scale-105 transition w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20"
            style={{ minWidth: "2.5rem", maxWidth: "5rem" }}
          />
          <span
            style={{ fontSize: "32px" }}
            className="font-extrabold logo-font text-lg sm:text-xl md:text-2xl text-blue-700 tracking-tight drop-shadow max-w-[7rem] sm:max-w-none"
          >
            RoomRounds
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="space-x-1 sm:space-x-2 items-center desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={handleLinkClick}
              className={`flex items-center gap-1 px-2 sm:px-3 py-2 rounded-lg transition font-medium text-sm sm:text-base hover:bg-blue-50 hover:text-blue-700 ${
                location.pathname === link.to
                  ? "bg-blue-100 text-blue-700 font-semibold shadow"
                  : "text-gray-700"
              }`}
            >
              {link.icon}
              <span className="truncate">{link.label}</span>
            </Link>
          ))}
          <Link
            to="/free-trial"
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 sm:px-6 py-2 rounded-lg shadow hover:from-blue-700 hover:to-blue-600 font-semibold transition ml-1 sm:ml-2 whitespace-nowrap text-sm sm:text-base"
          >
            <FaRocket className="mb-1" />
            Start Free Trial
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="mobile-menu-btn items-center text-blue-700 text-2xl focus:outline-none ml-2"
          onClick={handleMenuToggle}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.3, type: "spring" }}
            className="mobile-menu bg-white/95 backdrop-blur border-b border-blue-100 shadow-lg absolute top-full left-0 w-full z-50"
          >
            <div className="flex flex-col items-center py-4 space-y-2 w-full">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleLinkClick}
                  className={`flex items-center gap-2 px-4 py-3 w-11/12 text-base font-medium rounded hover:bg-blue-50 hover:text-blue-700 transition justify-center ${
                    location.pathname === link.to
                      ? "bg-blue-100 text-blue-700 font-semibold shadow"
                      : "text-gray-700"
                  }`}
                >
                  {link.icon}
                  <span className="truncate max-w-[10rem]">{link.label}</span>
                </Link>
              ))}
              <Link
                to="/free-trial"
                onClick={handleLinkClick}
                className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-3 rounded-lg shadow hover:from-blue-700 hover:to-blue-600 font-semibold transition w-11/12 justify-center whitespace-nowrap text-base"
              >
                <FaRocket className="mb-1" />
                Start Free Trial
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
